/**
 * Conversão entre hora de parede e instante, sem dependência externa.
 *
 * Vive em shared/ porque a agenda precisa da MESMA matemática nos dois lados:
 * o navegador interpreta "14:00 em Manaus" ao marcar, o servidor recalcula
 * horários livres e conflitos com o mesmo critério. Duas implementações
 * divergiriam justamente nos dois dias do ano em que o relógio muda.
 *
 * `Intl` cuida do horário de verão sozinho — por isso o offset é sempre medido
 * NO INSTANTE, nunca uma constante por fuso.
 */

export const FUSO_PADRAO = "America/Sao_Paulo";

/** Partes da hora de parede num fuso, em formato ISO local (sem offset). */
export function isoLocalNoFuso(date: Date, timezone: string): string {
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const p: Record<string, string> = {};
  for (const parte of fmt.formatToParts(date)) {
    if (parte.type !== "literal") p[parte.type] = parte.value;
  }
  // Intl devolve "24" para meia-noite com hour12:false em alguns runtimes.
  const hora = p.hour === "24" ? "00" : p.hour;
  return `${p.year}-${p.month}-${p.day}T${hora}:${p.minute}:${p.second}`;
}

/** Deslocamento do fuso, em ms, NAQUELE instante (positivo a leste). */
export function offsetMsNoFuso(date: Date, timezone: string): number {
  return Date.parse(`${isoLocalNoFuso(date, timezone)}Z`) - date.getTime();
}

/**
 * Hora de parede num fuso → instante UTC.
 *
 * Duas passagens de propósito: o offset depende do instante, e o instante é o
 * que queremos descobrir, então a primeira estimativa usa o offset da hora
 * errada. Nos dois dias do ano em que o relógio muda, a segunda corrige.
 */
export function horaLocalParaUtc(
  ano: number,
  mes: number, // 1-12
  dia: number,
  horas: number,
  minutos: number,
  timezone: string,
): Date {
  const comoSeFosseUtc = Date.UTC(ano, mes - 1, dia, horas, minutos, 0, 0);
  const off1 = offsetMsNoFuso(new Date(comoSeFosseUtc), timezone);
  let instante = comoSeFosseUtc - off1;
  const off2 = offsetMsNoFuso(new Date(instante), timezone);
  if (off2 !== off1) instante = comoSeFosseUtc - off2;
  return new Date(instante);
}

/** "YYYY-MM-DDTHH:mm" (valor de `datetime-local`) lido NO fuso indicado. */
export function inputLocalParaUtc(valor: string, timezone: string): Date {
  const [data, hora] = valor.split("T");
  const [a, m, d] = data.split("-").map(Number);
  const [hh, mi] = (hora ?? "00:00").split(":").map(Number);
  return horaLocalParaUtc(a, m, d, hh, mi, timezone);
}

/** Instante → "YYYY-MM-DDTHH:mm" no fuso indicado, para `datetime-local`. */
export function utcParaInputLocal(date: Date, timezone: string): string {
  return isoLocalNoFuso(date, timezone).slice(0, 16);
}

/** "HH:mm" do instante no fuso indicado. */
export function horaNoFuso(date: Date, timezone: string): string {
  return isoLocalNoFuso(date, timezone).slice(11, 16);
}

/** As duas horas de parede coincidem nesses fusos, neste instante? */
export function mesmaHoraDeParede(date: Date, fusoA: string, fusoB: string): boolean {
  return offsetMsNoFuso(date, fusoA) === offsetMsNoFuso(date, fusoB);
}

/** É um identificador de fuso que o runtime aceita? */
export function fusoValido(timezone: string | null | undefined): boolean {
  const tz = timezone?.trim();
  if (!tz) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/**
 * Fusos oferecidos na tela.
 *
 * Curada em vez de `Intl.supportedValuesOf("timeZone")`, que devolve ~400
 * entradas e transformaria "escolher o fuso da reunião" num garimpo. Cobre os
 * quatro offsets do Brasil e os destinos que aparecem em reunião com cliente
 * ou parceiro de fora. Quem precisar de outro continua podendo — a coluna
 * aceita qualquer identificador IANA válido; é só a LISTA que é curta.
 */
export const FUSOS_SUGERIDOS: Array<{ tz: string; label: string }> = [
  { tz: "America/Sao_Paulo", label: "Brasília (SP, RJ, MG, Sul, Nordeste)" },
  { tz: "America/Manaus", label: "Manaus (AM, RO, RR, MT, MS)" },
  { tz: "America/Rio_Branco", label: "Rio Branco (AC)" },
  { tz: "America/Noronha", label: "Fernando de Noronha" },
  { tz: "America/Belem", label: "Belém (PA, AP)" },
  { tz: "America/Fortaleza", label: "Fortaleza (CE, PI, RN, PB, PE, AL, SE)" },
  { tz: "America/Bahia", label: "Salvador (BA)" },
  { tz: "America/Cuiaba", label: "Cuiabá (MT)" },
  { tz: "America/Campo_Grande", label: "Campo Grande (MS)" },
  { tz: "America/Porto_Velho", label: "Porto Velho (RO)" },
  { tz: "America/Boa_Vista", label: "Boa Vista (RR)" },
  { tz: "UTC", label: "UTC" },
  { tz: "America/New_York", label: "Nova York" },
  { tz: "America/Los_Angeles", label: "Los Angeles" },
  { tz: "Europe/Lisbon", label: "Lisboa" },
  { tz: "Europe/London", label: "Londres" },
  { tz: "Europe/Madrid", label: "Madri" },
  { tz: "Europe/Paris", label: "Paris" },
];

/** Rótulo legível, caindo no próprio identificador quando não está na lista. */
export function rotuloDoFuso(timezone: string): string {
  return FUSOS_SUGERIDOS.find((f) => f.tz === timezone)?.label ?? timezone;
}

/** "UTC-3", "UTC+1" — o offset do fuso naquele instante, para exibição. */
export function offsetLegivel(date: Date, timezone: string): string {
  const minutos = Math.round(offsetMsNoFuso(date, timezone) / 60_000);
  if (minutos === 0) return "UTC";
  const sinal = minutos > 0 ? "+" : "-";
  const abs = Math.abs(minutos);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return `UTC${sinal}${h}${m ? `:${String(m).padStart(2, "0")}` : ""}`;
}
